import React, { useState, useMemo } from 'react';
import { Pressable, Platform, StyleSheet, TextInput, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';

const createStyles = (colors) => StyleSheet.create({
  numberControlContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    width: '100%'
  },
  numberValue: {
    textAlign: 'center',
    fontSize: 15,
    color: colors.text_color_primary,
    marginHorizontal: 8,
    minWidth: 45,
    backgroundColor: 'transparent',
    border: 'none',
    outlineStyle: 'none',
    ...Platform.select({
      web: {
        cursor: 'text',
      }
    })
  },
  numberButton: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 16,
    backgroundColor: colors.solid_color_primary,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }
    })
  },
  numberButtonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
});

const NumberControl = ({ param, onChange }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [isDecrementHovered, setIsDecrementHovered] = useState(false);
  const [isIncrementHovered, setIsIncrementHovered] = useState(false);
  const min = param.validation?.number?.min ?? 0;
  const max = param.validation?.number?.max ?? 100;
  const step = param.validation?.number?.step ?? 1;
  
  // Calculate decimal places based on step
  const getDecimalPlaces = (step) => {
    const stepStr = step.toString();
    const decimalIndex = stepStr.indexOf('.');
    return decimalIndex === -1 ? 0 : stepStr.length - decimalIndex - 1;
  };
  
  const decimalPlaces = getDecimalPlaces(step);
  
  const roundToStep = (value) => {
    return Number((Math.round(value / step) * step).toFixed(decimalPlaces));
  };
  
  const handleDecrement = () => {
    const newValue = roundToStep(Math.max(min, param.value - step));
    onChange(newValue);
  };
  
  const handleIncrement = () => {
    const newValue = roundToStep(Math.min(max, param.value + step));
    onChange(newValue);
  };

  const handleTextChange = (text) => {
    // Remove any non-numeric characters except decimal point and minus sign
    text = text.replace(/[^\d.-]/g, '');
    
    if (text === '' || text === '-' || text === '.') {
      return;
    }

    let value = Number(text);
    
    // Clamp value between min and max
    value = Math.max(min, Math.min(max, value));
    
    // Don't round to step for manual input
    onChange(value);
  };
  
  return (
    <View style={styles.numberControlContainer}>
      <Pressable 
        style={[
          styles.numberButton,
          isDecrementHovered && styles.numberButtonHovered
        ]} 
        onPress={handleDecrement}
        onHoverIn={() => setIsDecrementHovered(true)}
        onHoverOut={() => setIsDecrementHovered(false)}
      >
        <Icon name="minus" size={16} color={colors.text_color_primary} />
      </Pressable>
      
      <TextInput
        style={styles.numberValue}
        value={param?.value?.toFixed(decimalPlaces)}
        onChangeText={handleTextChange}
        keyboardType="numeric"
        selectTextOnFocus={Platform.OS !== 'macos'}
        {...Platform.select({
          web: {
            type: 'number',
            min: min,
            max: max,
            step: step
          }
        })}
      />
      
      <Pressable 
        style={[
          styles.numberButton,
          isIncrementHovered && styles.numberButtonHovered
        ]} 
        onPress={handleIncrement}
        onHoverIn={() => setIsIncrementHovered(true)}
        onHoverOut={() => setIsIncrementHovered(false)}
      >
        <Icon name="plus" size={16} color={colors.text_color_primary} />
      </Pressable>
    </View>
  );
};

export default NumberControl;

